/**
 * Configurazione di avvio conversazione di un connettore (mode × contenuto).
 *
 * Due assi indipendenti, come nello schema BotConnector:
 *  - `mode` = QUANDO il bot agisce (proactive = apertura conversazione,
 *    reactive = primo messaggio dell'utente);
 *  - contenuto (XOR) = COSA: `welcomeMessage` oppure `initialFlowId`.
 * Entrambi i contenuti sono validi in entrambe le modalità.
 */

/**
 * Costruisce il `conversationConfig` da inviare al POST del connettore, oppure
 * `null` se la configurazione persistita non è stata caricata.
 *
 * Il `null` è il punto centrale: quando la GET del connettore falliva, la vista
 * restava sui propri default (`mode: 'reactive'`, messaggio vuoto, nessun flusso)
 * e il primo salvataggio — anche solo di un colore — li scriveva sopra la
 * configurazione reale, azzerando il flusso iniziale in silenzio.
 * Vedi card #10278328553.
 *
 * @param {boolean} loaded se la configurazione persistita è stata caricata
 * @param {{mode: string, welcomeMessage: string, initialFlowId: ?string}} connectorConfig
 * @returns {?object} payload da inviare, o null per non toccare la configurazione
 */
export function buildConversationConfigPayload(loaded, connectorConfig) {
  if (!loaded || !connectorConfig) return null;
  return {
    mode: connectorConfig.mode,
    welcomeMessage: connectorConfig.welcomeMessage,
    initialFlowId: connectorConfig.initialFlowId || null,
  };
}

/**
 * Canali che gestiscono l'apertura con una UI propria, dove "né messaggio né flusso"
 * è una scelta legittima e non una configurazione rotta: WhatsApp non offre affatto il
 * messaggio di benvenuto, il voicebot ha "nessuna apertura" come terza opzione
 * esplicita, GPT-Live senza apertura configurata saluta da sé. Avvisare anche lì
 * renderebbe l'avviso rumore da ignorare. Live Chat non è un canale di conversazione
 * del bot (niente messaggio di benvenuto né flusso iniziale): l'avviso non ha senso.
 */
const START_CONTENT_OPTIONAL_TYPES = ['whatsapp', 'voicebot', 'gptlive', 'livechat'];

/**
 * Il canale impone la scelta messaggio-XOR-flusso? Solo su questi ha senso segnalare
 * un contenuto di avvio vuoto.
 *
 * @param {string} type tipo di connettore
 * @returns {boolean}
 */
export function startContentIsRequired(type) {
  return !START_CONTENT_OPTIONAL_TYPES.includes(type);
}

/**
 * Contenuto di avvio vuoto: né messaggio di benvenuto né flusso iniziale. Il bot
 * non fa la prima mossa e un flusso iniziale che idrata il contesto non parte —
 * quasi sempre un errore di configurazione, e completamente silenzioso a runtime.
 *
 * @param {?{welcomeMessage: ?string, initialFlowId: ?string}} conversationConfig
 * @returns {boolean}
 */
export function hasEmptyStartContent(conversationConfig) {
  if (!conversationConfig) return false;
  const { welcomeMessage, initialFlowId } = conversationConfig;
  return !initialFlowId && !String(welcomeMessage || '').trim();
}
